import { useMemo, useRef } from 'react';

import { makeMultiSorter } from '@cast/design-system';
import { Cluster } from '@cast/types';

import type { GetRowData } from './_types';

const makeCpuUtilizationMultiSorter = (getRowData: GetRowData) =>
  makeMultiSorter([
    {
      name: 'Allocatable',
      testId: 'allocatable',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id ?? '')?.summary?.cpuAllocatableTotal.toNumber() ??
        0,
    },
    {
      name: 'Used',
      testId: 'used',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id)?.summary?.cpuUsed.toNumber() ?? 0,
    },
    {
      name: 'Requested',
      testId: 'requested',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id)?.summary?.cpuRequestedTotal.toNumber() ?? 0,
    },
  ]);

const makeMemUtilizationMultiSorter = (getRowData: GetRowData) =>
  makeMultiSorter([
    {
      name: 'Allocatable',
      testId: 'allocatable',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id)?.summary?.ramAllocatableTotal.toNumber() ?? 0,
    },
    {
      name: 'Used',
      testId: 'used',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id)?.summary?.ramUsed.toNumber() ?? 0,
    },
    {
      name: 'Requested',
      testId: 'requested',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id)?.summary?.ramRequestedTotal.toNumber() ?? 0,
    },
  ]);

const makeGpuUtilizationMultiSorter = (getRowData: GetRowData) =>
  makeMultiSorter([
    {
      name: 'Allocatable',
      testId: 'allocatable',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id)?.summary?.gpuAllocatableTotal.toNumber() ?? 0,
    },
    {
      name: 'Requested',
      testId: 'requested',
      accessor: (cluster: Cluster) =>
        getRowData(cluster.id)?.summary?.gpuRequestedTotal.toNumber() ?? 0,
    },
  ]);

type UtilizationMultiSorter = ReturnType<typeof makeMultiSorter>;

// Don't rebuild the sorter components when the identity of getRowData
// changes: a rebuilt sorter remounts (icon + open menu). Build them once and
// read the latest getRowData through a ref.
/* eslint-disable react-hooks/refs */
export const useUtilizationMultiSorters = (
  getRowData: GetRowData
): {
  CpuUtilizationMultiSorter: UtilizationMultiSorter;
  MemUtilizationMultiSorter: UtilizationMultiSorter;
  GpuUtilizationMultiSorter: UtilizationMultiSorter;
} => {
  const getRowDataRef = useRef(getRowData);

  getRowDataRef.current = getRowData;

  const CpuUtilizationMultiSorter = useMemo(
    () =>
      makeCpuUtilizationMultiSorter((clusterId: string) =>
        getRowDataRef.current(clusterId)
      ),
    []
  );
  const MemUtilizationMultiSorter = useMemo(
    () =>
      makeMemUtilizationMultiSorter((clusterId: string) =>
        getRowDataRef.current(clusterId)
      ),
    []
  );
  const GpuUtilizationMultiSorter = useMemo(
    () =>
      makeGpuUtilizationMultiSorter((clusterId: string) =>
        getRowDataRef.current(clusterId)
      ),
    []
  );
  // see comment above for the explanation
  /* eslint-enable react-hooks/refs */

  return {
    CpuUtilizationMultiSorter,
    MemUtilizationMultiSorter,
    GpuUtilizationMultiSorter,
  };
};
